/**
 * Invitations API endpoints
 *
 * Provides CRUD operations for managing scheduled invitations.
 * An invitation sends content (packet or checklist) to recipients via a Share.
 *
 */

import type { FileRef } from '@/types/file';
import { z } from 'zod';
import { apiClient } from './client';
import { convertKeysToSnake } from '@/lib/dataTransform';
import { acquireIdempotencyKey, idempotencyErrorCode } from './idempotencyKey';
import type { RawPendingAssignmentRow } from '../components/analytics/types';

// ============================================
// Types - Match server InvitationSerializer
// ============================================

/**
 * Contact method for sending invitations
 * - email: Send via email
 * - phone: Send via SMS
 * - app: In-app notification only (packets only, not checklists)
 */
export type ContactMethod = 'email' | 'phone' | 'app';

/**
 * Sender information embedded in invitation response
 */
export interface InvitationSender {
  id: number;
  email: string;
  firstName: string;
  lastName?: string;
  avatar?: FileRef | null;
}

/**
 * Contact recipient in invitation
 */
export interface InvitationContact {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  emailId?: number;
  phoneId?: number;
}

/**
 * Filter used to select recipients
 */
export interface InvitationFilter {
  id: number;
  name: string;
  contactCount?: number;
}

/**
 * Label used to select recipients
 */
export interface InvitationLabel {
  name: string;
  contactCount?: number;
}

/**
 * A recipient *selection* that currently resolves to nobody (#216).
 *
 * - `label` — `name` is the label name
 * - `attribute` — `name` is the dimension, `value` the picked value
 *   (e.g. Department = Engineering)
 * - `filter` — `name` is the saved filter's name
 * - `contacts` — the individually picked contacts are all gone (archived);
 *   there is no per-contact breakdown, so `name` carries no useful detail
 */
export interface InvitationEmptySource {
  kind: 'label' | 'attribute' | 'filter' | 'contacts';
  name: string;
  value?: string | null;
}

/**
 * Invitation - matches server InvitationSerializer response
 *
 * An invitation is always linked to a Share. The share determines whether
 * the invitation is for a packet or checklist (share.type).
 */
export interface Invitation {
  /** Unique identifier */
  id: number;

  /** Share ID - required for creating/editing invitations */
  shareId: number;

  /** Title of the content being shared (from API) */
  contentTitle: string;

  /** Legacy alias — some code still references title */
  title?: string;

  /** Packet ID if this is a packet invitation (null for checklists) */
  packetId: number | null;

  /** Checklist ID if this is a checklist invitation (null for packets) */
  checklistId: number | null;

  /**
   * Content kind from the API (`packet` | `checklist`). Paired with
   * {@link contentId} on list items to resolve {@link packetId}/{@link checklistId}.
   */
  type?: string;

  /**
   * Polymorphic content id from the list endpoint (the packet or checklist id).
   * The list response carries `content_id` + `type` rather than the split
   * packetId/checklistId; {@link mapUnsentInvitationListItem} flattens it.
   */
  contentId?: number;

  /** User who created/sent this invitation */
  sender?: InvitationSender;

  /** Custom message included with invitation (max 280 chars) */
  message: string;

  /** Direct contact recipients */
  contacts: InvitationContact[];

  /** Filter-based recipients */
  contactFilters: InvitationFilter[];

  /** Label-based recipients */
  contactLabels: InvitationLabel[];

  /** Attribute-targeted recipients (dimension name + value) */
  contactAttributes?: InvitationAttributeSource[];

  /** Preferred method for contacting recipients */
  preferredContactMethod: ContactMethod;

  /**
   * Total number of recipients. The list/detail endpoints emit this as
   * `recipient_count` (→ `recipientCount`); `contactCount` is only set on the
   * create/update *response* ({@link CreateInvitationResult} /
   * {@link UpdateInvitationResult}) and on rows the client writes from one.
   * Read `recipientCount` for list display, falling back to `contactCount`.
   */
  contactCount: number;
  recipientCount?: number;

  /**
   * People the invitation reached when it was last saved (vingapi#2295,
   * `recipient_count_at_schedule`). `null` = no was-number recorded (every
   * invitation scheduled before it shipped): the row warns at 0 only. Unsent-list
   * only; {@link mapUnsentInvitationListItem} normalizes an absent field to null.
   */
  recipientCountAtSchedule?: number | null;

  /**
   * Whether this invitation currently resolves to ZERO recipients (#216).
   * Server-computed and re-verified live on every list response, so it is
   * never a stale zero. True only for an UNSENT invitation — a sent
   * invitation's zero is history and is never flagged.
   */
  audienceEmpty?: boolean;

  /**
   * Which of the invitation's recipient selections resolve to nobody, so the
   * UI can name what to go fix. May be empty even when {@link audienceEmpty}
   * is true (a draft with no selections at all names nothing).
   */
  emptySources?: InvitationEmptySource[];

  /**
   * True while the send task is processing this invitation (#875a, vingapi
   * #837 2b `InvitationListItem.resolving`): claimed, not yet sent, not
   * parked. The Scheduled row reads "checking recipients…" instead of the
   * count. Unsent-list only; {@link mapUnsentInvitationListItem} normalizes an
   * absent field to false.
   */
  resolving?: boolean;

  /** Scheduled send date/time (ISO string) — returned as scheduledDate from API */
  scheduledDate: string | null;

  /** Recurrence interval in days (null = one-time) */
  recurrenceInterval: number | null;

  /** Whether invitation can be edited (false if sending soon) */
  editable: boolean;

  /** Timestamp when actually sent (null = not sent yet) */
  sent?: string | null;

  /** Created timestamp */
  created?: string;

  /** Last modified timestamp */
  modified?: string;
}

// ============================================
// Invitation detail (GET /api/invitations/{id}/)
// ============================================

/**
 * A contact recipient as originally selected, from the detail endpoint's
 * `recipient_sources`. `emailId`/`phoneId` carry the per-contact delivery
 * override the picker stored; typed ad-hoc emails/phones round-trip here too
 * (create resolves each to a contact + method). No email/phone *string* is
 * provided — only the method id.
 */
export interface InvitationContactSource {
  contactId: number;
  name: string;
  emailId?: number | null;
  phoneId?: number | null;
}

/** A saved contact-filter recipient source (id + display name). */
export interface InvitationFilterSource {
  id: number;
  name: string;
}

/** An attribute-targeting recipient source (dimension name + value). */
export interface InvitationAttributeSource {
  name: string;
  value: string;
}

/**
 * The original recipient *selections* for an invitation — the create-time
 * source picks (not the resolved delivery recipients, which don't exist until
 * send), sufficient to rebuild the recipient picker when editing.
 */
export interface InvitationRecipientSources {
  contacts: InvitationContactSource[];
  filters: InvitationFilterSource[];
  labels: string[];
  attributes: InvitationAttributeSource[];
}

/**
 * Raw response from `GET /api/invitations/{id}/` (vingapi `InvitationDetail`),
 * after the api client camelCases it. Distinct from the create/update *response*
 * — this is the invitation's full detail, including the recipient selections and
 * recurrence needed to repopulate the edit dialog. Mapped into {@link Invitation}
 * by {@link mapInvitationDetailToInvitation}.
 */
export interface InvitationDetailResponse {
  id: number;
  status: string;
  type: string;
  scheduledDate: string | null;
  sentDate: string | null;
  deliveryMethod: string;
  message: string | null;
  subject?: string | null;
  recipientCount: number;
  shareStub: string;
  contentTitle: string;
  contentId: number;
  labels: string[];
  recurrenceInterval: number | null;
  recipientSources: InvitationRecipientSources;
  /** See {@link Invitation.audienceEmpty}. Optional — an older server omits it. */
  audienceEmpty?: boolean;
  /** See {@link Invitation.emptySources}. Optional — an older server omits it. */
  emptySources?: InvitationEmptySource[];
  /** See {@link Invitation.recipientCountAtSchedule}. Always present on the server's detail (null = none recorded). */
  recipientCountAtSchedule?: number | null;
}

const VALID_CONTACT_METHODS: ContactMethod[] = ['email', 'phone', 'app'];

/**
 * Map the detail endpoint's response into the {@link Invitation} shape the
 * ShareDialog edit flow consumes. The detail exposes recipients as
 * `recipientSources` (contacts/filters/labels) — fan those out to
 * `contacts`/`contactFilters`/`contactLabels`, and `deliveryMethod` to
 * `preferredContactMethod`.
 */
export function mapInvitationDetailToInvitation(d: InvitationDetailResponse): Invitation {
  const method = VALID_CONTACT_METHODS.includes(d.deliveryMethod as ContactMethod)
    ? (d.deliveryMethod as ContactMethod)
    : 'email';

  return {
    id: d.id,
    // The detail endpoint exposes share_stub, not the numeric share id. Edit
    // callers pass shareId as a separate ShareDialog prop (from the list item),
    // so this is a never-read placeholder kept only to satisfy the type.
    shareId: 0,
    contentTitle: d.contentTitle,
    packetId: d.type === 'packet' ? d.contentId : null,
    checklistId: d.type === 'checklist' ? d.contentId : null,
    message: d.message ?? '',
    contacts: d.recipientSources.contacts.map((c) => ({
      id: c.contactId,
      name: c.name,
      emailId: c.emailId ?? undefined,
      phoneId: c.phoneId ?? undefined,
    })),
    contactFilters: d.recipientSources.filters.map((f) => ({ id: f.id, name: f.name })),
    contactLabels: d.recipientSources.labels.map((name) => ({ name })),
    contactAttributes: d.recipientSources.attributes.map((a) => ({ name: a.name, value: a.value })),
    preferredContactMethod: method,
    contactCount: d.recipientCount,
    recipientCount: d.recipientCount,
    // Absent reads as "not empty": a server that predates #216 must never make
    // the UI claim a send will reach nobody.
    audienceEmpty: d.audienceEmpty ?? false,
    emptySources: d.emptySources ?? [],
    // #1549-b: the edit dialog's was-number. Absent or null = none recorded; a recorded 0 stays 0.
    recipientCountAtSchedule: d.recipientCountAtSchedule ?? null,
    scheduledDate: d.scheduledDate,
    recurrenceInterval: d.recurrenceInterval,
    // Editability is enforced server-side and surfaced by the list item; the
    // edit dialog only opens for an already-editable invitation.
    editable: true,
  };
}

/**
 * Parameters for listing invitations
 */
export interface InvitationListParams {
  /** Filter by contact filter IDs */
  contactFilters?: string;
  /** Filter by contact label names */
  contactLabels?: string;
  /** Filter by contact IDs */
  contactIds?: string;
  /** Filter by email addresses */
  emails?: string;
  /** Filter by phone numbers */
  phones?: string;
  /** Filter by packet IDs */
  packetIds?: string;
  /** Filter by checklist IDs */
  checklistIds?: string;
}

/**
 * Response from unsent invitations endpoints
 */
export interface UnsentInvitationsResponse {
  results: Invitation[];
}

/**
 * Payload for creating an invitation
 * Field names use snake_case suffixes (_data) as expected by server
 */
export interface CreateInvitationPayload {
  /** Share ID (required) */
  shareId: number;

  /**
   * Send the invitation as another user (staff only). Absent or equal to the
   * caller's own id: normal send. A different id: the invitation is sent as
   * that user. Non-staff callers receive 403 from the server.
   * @see vingapi#771
   */
  senderId?: number;

  /** Preferred contact method */
  preferredContactMethod: ContactMethod;

  /** Send date - ISO string or "now" for immediate */
  sendDate: string;

  /** Custom message (max 280 chars) */
  message?: string;

  /** Direct email addresses */
  emails?: string[];

  /** Direct phone numbers (E.164 format) */
  phones?: string[];

  /** Contact IDs to invite */
  contactsData?: number[];

  /** Contacts with specific delivery method overrides */
  contactMethodsData?: { contactId: number; emailId?: number; phoneId?: number }[];

  /** Filter IDs to use for recipient selection */
  contactFiltersData?: number[];

  /** Label names to use for recipient selection */
  contactLabelsData?: string[];

  /** Attribute (dimension, value) targets to use for recipient selection */
  contactAttributesData?: { name: string; value: string }[];

  /** Recurrence interval in days */
  recurrenceInterval?: number;

  /**
   * Analytics filter for bulk selection from analytics tables.
   * Server resolves contacts from the filter URL and creates invitation.
   */
  analyticsFilter?: {
    /** The request URL used to fetch the analytics data (defines the filter) */
    requestUrl: string;
    /** Number of items selected (for validation) */
    count: number;
    /** IDs explicitly selected (when not using select-all) */
    selectedIds?: number[];
    /** IDs to exclude from selection (when using select-all) */
    excludedIds?: number[];
  };

  /**
   * Remind (vingapi#2147 S2): the server leaves out everyone who has already
   * completed the share, for explicit ids and select-all alike. Absent = false.
   */
  excludeCompleted?: boolean;
}

/**
 * The recipient part of the create payload: what decides WHO a send reaches.
 * The send and the reachability preview are built from the same object
 * (`buildRecipientFields` in ShareDialog), so the preview cannot drift from
 * the send.
 */
export type InvitationRecipientFields = Pick<
  CreateInvitationPayload,
  | 'shareId'
  | 'preferredContactMethod'
  | 'analyticsFilter'
  | 'contactsData'
  | 'contactMethodsData'
  | 'excludeCompleted'
>;

/**
 * Payload for updating an invitation
 */
export interface UpdateInvitationPayload {
  /** Updated send date */
  sendDate?: string;

  /** Updated message */
  message?: string;

  /** Updated contact method */
  preferredContactMethod?: ContactMethod;

  /** Updated direct emails */
  emails?: string[];

  /** Updated direct phones */
  phones?: string[];

  /** Updated contact IDs */
  contactsData?: number[];

  /** Updated contacts with specific delivery method overrides */
  contactMethodsData?: { contactId: number; emailId?: number; phoneId?: number }[];

  /** Updated filter IDs */
  contactFiltersData?: number[];

  /** Updated label names */
  contactLabelsData?: string[];

  /** Updated attribute (dimension, value) targets */
  contactAttributesData?: { name: string; value: string }[];

  /** Updated recurrence interval */
  recurrenceInterval?: number | null;
}

// ============================================
// Create / update responses (vingreact#962)
// ============================================

/**
 * `POST /api/invitations/` response — vingapi src/consume/invitation/routers.py
 * `create_invitation` returns exactly `{id, contact_count, processing_queued,
 * pending_assignments}` (camelCased by the client). It is NOT an
 * {@link Invitation}: no content id, title, date or recurrence. A consumer
 * that needs those reads them from its own request (useCreateInvitation seeds
 * the Scheduled row from its variables). vingapi#1452 will type it in OpenAPI.
 */
export interface CreateInvitationResult {
  id: number;
  /** Recipients the create resolved. */
  contactCount: number;
  /** True for an immediate ("now") send: dispatch is already queued. */
  processingQueued: boolean;
  /**
   * Preview of the assignments this invitation will create — populated only
   * when `processingQueued` is true (immediate send). Real Assignment rows
   * aren't created synchronously; this lets the UI show a placeholder
   * immediately instead of waiting on the async dispatch cycle. See
   * vingapi/docs/invitation-pending-assignments-api.md.
   */
  pendingAssignments: RawPendingAssignmentRow[];
}

/**
 * `PUT /api/invitations/{id}/` response (update, reschedule and send-now all
 * use it) — vingapi routers.py `update_invitation` returns exactly
 * `{id, contact_count, fields_updated}`. NOT an {@link Invitation}: never
 * cache it as a detail or spread it over a row.
 */
export interface UpdateInvitationResult {
  id: number;
  /** The server's recipient count after the update (recounted when the request named recipients). */
  contactCount: number;
  /** Snake-case names of the scalar fields the update changed (e.g. `send_date`). */
  fieldsUpdated: string[];
}

// ============================================
// API Functions
// ============================================

/**
 * Get a single invitation by ID
 * GET /api/invitations/{id}/
 */
export async function getInvitation(id: number): Promise<Invitation> {
  const response = await apiClient.get<InvitationDetailResponse>(`/api/invitations/${id}/`);
  return mapInvitationDetailToInvitation(response.data);
}

const IN_PROGRESS_WAITS_MS = [1000, 2000, 4000, 8000];
const IN_PROGRESS_BUDGET_MS = 15_000;

function retryAfterMs(error: unknown): number | undefined {
  const raw = (error as { response?: { headers?: Record<string, unknown> } })?.response?.headers?.['retry-after'];
  const seconds = typeof raw === 'string' || typeof raw === 'number' ? Number(raw) : NaN;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : undefined;
}

/**
 * POST with the intent's `Idempotency-Key` (#1076-b, vingapi#2468 S1). Only a
 * 2xx or the server refusing the key itself (`idempotency_key_reused` /
 * `_invalid`) releases the key; every other failure keeps it so the next press
 * joins the same intent. A 409 `idempotency_key_in_progress` is repeated with
 * the same key (1/2/4/8 s, no repeat started after 15 s from the first 409);
 * still in progress then, the 409 is thrown with the key kept.
 */
async function postWithIdempotencyKey<T>(endpoint: string, payload: unknown): Promise<T> {
  const intent = acquireIdempotencyKey(endpoint, convertKeysToSnake(payload));
  const headers = { 'Idempotency-Key': intent.key };
  let deadline: number | undefined;
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await apiClient.post<T>(endpoint, payload, { headers });
      intent.release();
      return response.data;
    } catch (error) {
      const code = idempotencyErrorCode(error);
      if (code === 'idempotency_key_reused' || code === 'idempotency_key_invalid') {
        intent.release();
        throw error;
      }
      if (code !== 'idempotency_key_in_progress') throw error;
      deadline ??= Date.now() + IN_PROGRESS_BUDGET_MS;
      const wait = retryAfterMs(error) ?? IN_PROGRESS_WAITS_MS[attempt];
      if (wait === undefined || Date.now() + wait > deadline) throw error;
      await new Promise<void>((resolve) => setTimeout(resolve, wait));
    }
  }
}

/**
 * @flow_step
 * @touches Invitation:create
 * @upstream UI:ShareDialog
 * @downstream consume.invitation.pipeline.send
 * @intent Submits invitation to backend for scheduling or immediate send
 * @intent sendDate="now" triggers immediate send; future date schedules it
 * @intent Backend resolves recipients from contacts, filters, labels
 * @intent On success, backend creates recipients and triggers assignment creation cascade
 *
 * POST /api/invitations/
 */
export async function createInvitation(payload: CreateInvitationPayload): Promise<CreateInvitationResult> {
  return postWithIdempotencyKey<CreateInvitationResult>('/api/invitations/', payload);
}

const InvitationReachabilitySchema = z.object({
  count: z.number(),
  unreachableContactIds: z.array(z.number()),
  completedContactIds: z.array(z.number()),
  completedCount: z.number(),
});

export type InvitationReachability = z.infer<typeof InvitationReachabilitySchema>;

/**
 * Who the invitation POST for these recipients would reach, before anything is
 * created (vingapi#2147). Read-only. The server decides; callers render from
 * `unreachableContactIds` / `completedContactIds` and infer nothing themselves.
 * POST /api/invitations/reachability/
 */
export async function checkInvitationReachability(
  body: InvitationRecipientFields,
): Promise<InvitationReachability> {
  const response = await apiClient.post<unknown>('/api/invitations/reachability/', body);
  return InvitationReachabilitySchema.parse(response.data);
}

const AudienceCountSchema = z.object({ count: z.number().int() });

/**
 * The body of POST /api/invitations/audience-count/ in edit mode (vingapi#2295
 * S2): the invitation being edited plus the recipient fields the PUT would save
 * (built from the same object in ShareDialog). `emails`, `phones` and
 * `analyticsFilter` are not part of it: the server answers 422 to them.
 */
export type AudienceCountBody = { invitationId: number } & Pick<
  UpdateInvitationPayload,
  | 'contactsData'
  | 'contactMethodsData'
  | 'contactFiltersData'
  | 'contactLabelsData'
  | 'contactAttributesData'
>;

/**
 * How many people the edit dialog's current picks would reach (Q-581 item 3).
 * Read-only; `signal` cancels a replaced request.
 * POST /api/invitations/audience-count/
 */
export async function countInvitationAudience(
  body: AudienceCountBody,
  signal?: AbortSignal,
): Promise<number> {
  const response = await apiClient.post<unknown>('/api/invitations/audience-count/', body, { signal });
  return AudienceCountSchema.parse(response.data).count;
}

/**
 * Update an existing invitation
 * PUT /api/invitations/{id}/
 *
 * Note: Can only edit invitations where editable=true (send_date > 5 min away)
 */
export async function updateInvitation(
  id: number,
  payload: UpdateInvitationPayload
): Promise<UpdateInvitationResult> {
  const response = await apiClient.put<UpdateInvitationResult>(`/api/invitations/${id}/`, payload);
  return response.data;
}

/**
 * Delete an invitation
 * DELETE /api/invitations/{id}/
 *
 * Note: Cannot delete sent invitations
 *
 * @param scheduleRecurrence - Recurring invitations only. `true` = "delete this
 *   one, keep the series": the server creates the next occurrence before
 *   deleting this one. `false` (and the server default) = delete and end the
 *   series. Callers should pass it EXPLICITLY rather than lean on the default:
 *   this parameter once defaulted to `true` server-side, which is how a plain
 *   delete respawned the invitation it had just removed (QA 2026-08-26).
 */
export async function deleteInvitation(id: number, scheduleRecurrence?: boolean): Promise<void> {
  await apiClient.delete(`/api/invitations/${id}/`, {
    params: scheduleRecurrence !== undefined ? { schedule_recurrence: scheduleRecurrence } : undefined,
  });
}

/**
 * Flatten a list item's polymorphic `content_id` + `type` into the split
 * `packetId`/`checklistId` the UI navigates with. The unsent list endpoint
 * exposes `content_id` + `type` (not the split ids), so without this a row has
 * no resolvable entity id — title-click navigation would target id 0.
 */
export function mapUnsentInvitationListItem(item: Invitation): Invitation {
  return {
    ...item,
    packetId: item.type === 'packet' ? (item.contentId ?? null) : (item.packetId ?? null),
    checklistId: item.type === 'checklist' ? (item.contentId ?? null) : (item.checklistId ?? null),
    // Recurrence drives the delete dialog's "this one / entire series" choice.
    // The field is optional on the wire (an older server omits it entirely), so
    // normalize the absent case to an explicit null here rather than letting
    // `undefined` flow through a property the Invitation type promises is
    // `number | null`. Absent reads as one-off — the safe branch, which deletes
    // and ends there — never as "silently keep the series".
    recurrenceInterval: item.recurrenceInterval ?? null,
    // #216: the empty-audience verdict and the selections that produced it.
    // Both are optional on the wire; absent normalizes to "not empty" so an
    // older server can never render a false "will reach nobody" warning.
    audienceEmpty: item.audienceEmpty ?? false,
    emptySources: item.emptySources ?? [],
    // #875a: optional on the wire (default false; an older server omits it).
    // Absent reads as "not resolving" — the row shows its count as before.
    resolving: item.resolving ?? false,
    // #1549: absent or null = no was-number; never 0-as-missing (a recorded 0 stays 0).
    recipientCountAtSchedule: item.recipientCountAtSchedule ?? null,
  };
}

function mapUnsentResponse(data: UnsentInvitationsResponse): UnsentInvitationsResponse {
  return { ...data, results: data.results.map(mapUnsentInvitationListItem) };
}

/**
 * List unsent (scheduled) invitations for packets
 * GET /api/invitations/packet/unsent/
 */
export async function listUnsentPacketInvitations(
  params: InvitationListParams = {}
): Promise<UnsentInvitationsResponse> {
  const response = await apiClient.get<UnsentInvitationsResponse>(
    '/api/invitations/packet/unsent/',
    { params }
  );
  return mapUnsentResponse(response.data);
}

/**
 * List unsent (scheduled) invitations for checklists
 * GET /api/invitations/checklist/unsent/
 */
export async function listUnsentChecklistInvitations(
  params: InvitationListParams = {}
): Promise<UnsentInvitationsResponse> {
  const response = await apiClient.get<UnsentInvitationsResponse>(
    '/api/invitations/checklist/unsent/',
    { params }
  );
  return mapUnsentResponse(response.data);
}

/**
 * Reschedule an invitation to a new date
 * PUT /api/invitations/{id}/
 */
export async function rescheduleInvitation(id: number, newDate: Date): Promise<UpdateInvitationResult> {
  return updateInvitation(id, {
    sendDate: newDate.toISOString(),
  });
}

/**
 * Send an invitation immediately
 * PUT /api/invitations/{id}/
 */
export async function sendInvitationNow(id: number): Promise<UpdateInvitationResult> {
  return updateInvitation(id, {
    sendDate: 'now',
  });
}

// ============================================
// Bulk Invitation Types
// ============================================

/**
 * Per-share settings for bulk invitation creation
 */
export interface BulkShareEntry {
  shareId: number;
  sendDate?: string;
}

/**
 * Payload for creating invitations to multiple shares at once.
 * Shared fields (message, recipients) apply to ALL invitations.
 * Per-share fields (sendDate) are in the shares list.
 */
export interface BulkInvitationPayload {
  /**
   * Send the invitation as another user (staff only). Absent or equal to the
   * caller's own id: normal send. A different id: the invitation is sent as
   * that user. Non-staff callers receive 403 from the server.
   * @see vingapi#771
   */
  senderId?: number;
  preferredContactMethod?: ContactMethod;
  message?: string;
  subject?: string;
  contactsData?: number[];
  contactMethodsData?: { contactId: number; emailId?: number; phoneId?: number }[];
  contactFiltersData?: number[];
  contactLabelsData?: string[];
  contactAttributesData?: { name: string; value: string }[];
  emails?: string[];
  phones?: string[];
  shares: BulkShareEntry[];
}

/**
 * Result entry for a single invitation within a bulk creation
 */
export interface BulkInvitationResultEntry {
  shareId: number;
  invitationId: number;
  contactCount: number;
  processingQueued: boolean;
}

/**
 * Response from POST /api/invitations/bulk/
 */
export interface BulkInvitationResult {
  created: number;
  invitations: BulkInvitationResultEntry[];
}

// ============================================
// Bulk API Functions
// ============================================

/**
 * @flow_step
 * @touches Invitation:create
 * @upstream UI:BulkShareDialog
 * @downstream consume.invitation.pipeline.send
 * @intent Creates one invitation per share entry, all sharing the same recipients and message
 * @intent Each invitation enters the send pipeline independently
 * @intent Backend creates recipients and triggers assignment creation per share
 *
 * POST /api/invitations/bulk/
 */
export async function createBulkInvitation(
  payload: BulkInvitationPayload
): Promise<BulkInvitationResult> {
  return postWithIdempotencyKey<BulkInvitationResult>('/api/invitations/bulk/', payload);
}

// ============================================
// Convenience exports
// ============================================

export const invitationsApi = {
  getById: getInvitation,
  create: createInvitation,
  bulkCreate: createBulkInvitation,
  update: updateInvitation,
  delete: deleteInvitation,
  listUnsentPackets: listUnsentPacketInvitations,
  listUnsentChecklists: listUnsentChecklistInvitations,
  reschedule: rescheduleInvitation,
  sendNow: sendInvitationNow,
};
